import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Candidates from './pages/Candidates';
import Employers from './pages/Employers';
import Features from './pages/Features';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Blog from './pages/Blog';
import NotFound from './pages/NotFound';
import MarketingLayout from './components/MarketingLayout';

// Import from company domain
import {
    Companies,
    // CompanyOnboarding, // DEPRECATED
    CompanyProfile,
    // HiringPreferences, // DEPRECATED
    JobPostingMethod,
    JobPosting,
    JobPostings,
    JobReview,
    JobSuccess,
    CompanyJobDetails,
    CandidateMatches,
    AllRecommendations,
    InviteTeam,
    TalkToDavid,
    Messages as CompanyMessages,
    ReceivedApplications,
    COMPANY_ROUTES
} from './company';
import Projects from './company/pages/Projects';
import ProjectDetails from './company/pages/ProjectDetails';
import CompanyDashboard from './company/pages/Dashboard';
import CompanyCandidateSearch from './company/pages/CandidateSearch';
import ApplicationsReview from './company/pages/ApplicationsReview';
import CompanyDavidInbox from './company/pages/CompanyDavidInbox';

// Public Company Profile (no auth required)
import { PublicCompanyProfile } from './company/publicView';
// Company Auth
import { CompanySignUp, CompanySignUpSuccess, CompanyCheckEmail, CompanyProfileSetup, CompanyAdminRecruiterSetup } from './company/auth';

// Recruiter Auth
import {
    RecruiterAccountSetup,
    CompanyAdminWelcome,
    RecruiterJoinTeam,
    RecruiterWelcome
} from './recruiter/auth';

// Import Account Settings
import AccountSettings from './candidate/pages/AccountSettings';
import { SETTINGS_ROUTES } from './shared/settings/constants';
import { CompanyAccountSettings } from './company/settings';

// Candidate imports
import {
    Dashboard,
    Messages,
    MyProfile,
    Jobs,
    Applications,
    CANDIDATE_ROUTES,
} from './candidate';

// Candidate Auth
import { CandidateSignUp, CandidateSignUpSuccess } from './candidate/auth';


// Shared Auth (role-agnostic)
import {
    SignIn,
    ForgotPassword,
    CheckInbox,
    ResetPassword,
    ResetSuccess,
    CreatePassword,
    SetupPassword,
    JoinSuccess,
    ApplicationForm,
    ApplicationOTP,
    ApplicationSuccess,
    AUTH_ROUTES
} from './shared/auth';

import {
    WelcomeIntro,
    ResumeUpload as WelcomeResumeUpload,
    ResumeConfirmation,
    ProfileReview as WelcomeProfileReview,
    CareerGoals as WelcomeCareerGoals,
    OnboardingChat,
    WELCOME_ROUTES
} from './shared/welcome';

// Admin imports
import {
    AdminDashboard,
    ReviewCandidates,
    ReviewCompanies,
    CandidateSearch as AdminCandidateSearch,
    CandidateRecommendations as AdminCandidateRecommendations,
    DavidInbox as AdminDavidInbox,
    ManageJobs,
    SystemSettings,
    Analytics,
    UserManagement,
    AdminSetupPassword,
    ADMIN_ROUTES
} from './admin';
import ProtectedAdminRoute from './admin/components/ProtectedAdminRoute';
import SessionExpiredModal from './shared/auth/components/SessionExpiredModal';
import ProtectedCompanyRoute from './company/components/ProtectedCompanyRoute';
import ProtectedCandidateRoute from './candidate/components/ProtectedCandidateRoute';

// Component to handle Supabase auth redirects at root URL
const AuthRedirectHandler: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Check if we're at root with auth hash fragments (Supabase recovery/magic link)
        if (location.pathname === '/' && location.hash) {
            const hashParams = new URLSearchParams(location.hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const error = hashParams.get('error');
            const errorCode = hashParams.get('error_code');
            const type = hashParams.get('type');

            // Handle password recovery redirect
            if (accessToken && type === 'recovery') {
                // Redirect to reset password page with the hash intact
                navigate(`/auth/reset-password${location.hash}`, { replace: true });
                return;
            }

            // Handle magic link / email confirmation redirect
            if (accessToken) {
                // Could be a recovery or magic link - redirect to reset password
                navigate(`/auth/reset-password${location.hash}`, { replace: true });
                return;
            }

            // Handle auth errors
            if (error || errorCode) {
                navigate(`/auth/forgot-password${location.hash}`, { replace: true });
                return;
            }
        }
    }, [location, navigate]);

    return null;
};

function App() {
  return (
    <Router>
      <AuthRedirectHandler />
      <ScrollToTop />
      <SessionExpiredModal />
      <Routes>
        {/* Marketing pages — share Navbar + animated SeamlessBackground via layout */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/candidates" element={<Candidates />} />
          <Route path="/employers" element={<Employers />} />
          <Route path="/about" element={<About />} />
          {/* Catch-all 404 — must be the last child of the layout */}
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/features" element={<Features />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/blog" element={<Blog />} />
        
        {/* Public Company Profile - accessible without auth */}
        <Route path="/c/:companyId" element={<PublicCompanyProfile />} />

        {/* Auth Routes */}
        <Route path={AUTH_ROUTES.LOGIN} element={<SignIn />} />
        <Route path={AUTH_ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={AUTH_ROUTES.CHECK_INBOX} element={<CheckInbox />} />
        <Route path={AUTH_ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
        <Route path={AUTH_ROUTES.RESET_SUCCESS} element={<ResetSuccess />} />
        <Route path={AUTH_ROUTES.CREATE_PASSWORD} element={<CreatePassword />} />
        <Route path={AUTH_ROUTES.SETUP_PASSWORD} element={<SetupPassword />} />
        <Route path={AUTH_ROUTES.JOIN_SUCCESS} element={<JoinSuccess />} />
        <Route path={AUTH_ROUTES.APPLICATION_FORM} element={<ApplicationForm />} />
        <Route path={AUTH_ROUTES.APPLICATION_OTP} element={<ApplicationOTP />} />
        <Route path={AUTH_ROUTES.APPLICATION_SUCCESS} element={<ApplicationSuccess />} />

        {/* Welcome Onboarding Routes */}
        <Route path={WELCOME_ROUTES.INTRO} element={<WelcomeIntro />} />
        <Route path={WELCOME_ROUTES.RESUME_UPLOAD} element={<WelcomeResumeUpload />} />
        <Route path={WELCOME_ROUTES.RESUME_CONFIRMATION} element={<ResumeConfirmation />} />
        <Route path={WELCOME_ROUTES.PROFILE_REVIEW} element={<WelcomeProfileReview />} />
        <Route path={WELCOME_ROUTES.CAREER_GOALS} element={<WelcomeCareerGoals />} />
        <Route path={WELCOME_ROUTES.ONBOARDING_CHAT} element={<OnboardingChat />} />

        {/* Role-Specific Signup Routes */}
        <Route path="/auth/candidate/signup" element={<CandidateSignUp />} />
        <Route path="/auth/candidate/signup-success" element={<CandidateSignUpSuccess />} />
        <Route path="/auth/company/signup" element={<CompanySignUp />} />
        <Route path="/auth/company/check-email" element={<CompanyCheckEmail />} />
        <Route path="/auth/company/success" element={<CompanySignUpSuccess />} />
        <Route path="/auth/company/profile-setup" element={<CompanyProfileSetup />} />
        {/* Keep legacy route for backward compatibility */}
        <Route path={AUTH_ROUTES.REGISTER} element={<CandidateSignUp />} />

        {/* Recruiter Auth Routes */}
        <Route path="/auth/recruiter/account-setup" element={<RecruiterAccountSetup />} />
        <Route path="/auth/company/admin-recruiter-setup" element={<CompanyAdminRecruiterSetup />} />
        <Route path="/auth/company/recruiter-setup" element={<CompanyAdminRecruiterSetup />} />
        <Route path="/auth/company/admin-welcome" element={<CompanyAdminWelcome />} />
        <Route path="/auth/recruiter/join" element={<RecruiterJoinTeam />} />
        <Route path="/auth/recruiter/welcome" element={<RecruiterWelcome />} />

        {/* Account Settings Routes */}
        {/* Candidate uses shared AccountSettings - protected */}
        <Route path={SETTINGS_ROUTES.ACCOUNT_SETTINGS} element={<ProtectedCandidateRoute><AccountSettings /></ProtectedCandidateRoute>} />
        <Route path={SETTINGS_ROUTES.ACCOUNT_SETTINGS_CANDIDATE} element={<ProtectedCandidateRoute><AccountSettings /></ProtectedCandidateRoute>} />
        {/* Company uses CompanyAccountSettings with company-specific fields */}
        <Route path={SETTINGS_ROUTES.ACCOUNT_SETTINGS_COMPANY} element={<ProtectedCompanyRoute><CompanyAccountSettings /></ProtectedCompanyRoute>} />

        {/* Company Domain Routes - Protected */}
                <Route path={COMPANY_ROUTES.COMPANIES} element={<ProtectedCompanyRoute><Companies /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.DASHBOARD} element={<ProtectedCompanyRoute><CompanyDashboard /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.COMPANY_PROFILE} element={<ProtectedCompanyRoute><CompanyProfile /></ProtectedCompanyRoute>} />
                {/* DEPRECATED: These onboarding routes are no longer used in the current flow
                <Route path={COMPANY_ROUTES.ONBOARDING} element={<ProtectedCompanyRoute><CompanyOnboarding /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.HIRING_PREFERENCES} element={<ProtectedCompanyRoute><HiringPreferences /></ProtectedCompanyRoute>} />
                END DEPRECATED */}
                <Route path={COMPANY_ROUTES.JOB_POSTING_METHOD} element={<ProtectedCompanyRoute><JobPostingMethod /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.JOB_POSTING} element={<ProtectedCompanyRoute><JobPosting /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.JOB_POSTINGS} element={<ProtectedCompanyRoute><JobPostings /></ProtectedCompanyRoute>} />
                <Route path="/companies/jobs/:jobId" element={<ProtectedCompanyRoute><CompanyJobDetails /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.PROJECTS} element={<ProtectedCompanyRoute><Projects /></ProtectedCompanyRoute>} />
                <Route path="/companies/projects/:projectId" element={<ProtectedCompanyRoute><ProjectDetails /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.JOB_REVIEW} element={<ProtectedCompanyRoute><JobReview /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.JOB_SUCCESS} element={<ProtectedCompanyRoute><JobSuccess /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.CANDIDATE_MATCHES} element={<ProtectedCompanyRoute><CandidateMatches /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.ALL_RECOMMENDATIONS} element={<ProtectedCompanyRoute><AllRecommendations /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.INVITE_TEAM} element={<ProtectedCompanyRoute><InviteTeam /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.MESSAGES} element={<ProtectedCompanyRoute><CompanyMessages /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.RECEIVED_APPLICATIONS} element={<ProtectedCompanyRoute><ReceivedApplications /></ProtectedCompanyRoute>} />
                <Route path="/companies/talk-to-david" element={<ProtectedCompanyRoute><TalkToDavid /></ProtectedCompanyRoute>} />
                <Route path="/companies/candidate-search" element={<ProtectedCompanyRoute><CompanyCandidateSearch /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.APPLICATIONS_REVIEW} element={<ProtectedCompanyRoute><ApplicationsReview /></ProtectedCompanyRoute>} />
                <Route path={COMPANY_ROUTES.DAVID_INBOX} element={<ProtectedCompanyRoute><CompanyDavidInbox /></ProtectedCompanyRoute>} />

        {/* Candidate Domain Routes - Protected */}
        <Route path={CANDIDATE_ROUTES.DASHBOARD} element={<ProtectedCandidateRoute><Dashboard /></ProtectedCandidateRoute>} />
        <Route path={CANDIDATE_ROUTES.MY_PROFILE} element={<ProtectedCandidateRoute><MyProfile /></ProtectedCandidateRoute>} />
        <Route path={CANDIDATE_ROUTES.JOBS} element={<ProtectedCandidateRoute><Jobs /></ProtectedCandidateRoute>} />
        <Route path={CANDIDATE_ROUTES.APPLICATIONS} element={<ProtectedCandidateRoute><Applications /></ProtectedCandidateRoute>} />
        <Route path={CANDIDATE_ROUTES.MESSAGES} element={<ProtectedCandidateRoute><Messages /></ProtectedCandidateRoute>} />

        {/* Admin Domain Routes - Protected */}
        <Route path={ADMIN_ROUTES.DASHBOARD} element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.REVIEW_CANDIDATES} element={<ProtectedAdminRoute><ReviewCandidates /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.REVIEW_COMPANIES} element={<ProtectedAdminRoute><ReviewCompanies /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.CANDIDATE_SEARCH} element={<ProtectedAdminRoute><AdminCandidateSearch /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.CANDIDATE_RECOMMENDATIONS} element={<ProtectedAdminRoute><AdminCandidateRecommendations /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.DAVID_INBOX} element={<ProtectedAdminRoute><AdminDavidInbox /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.MANAGE_JOBS} element={<ProtectedAdminRoute><ManageJobs /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.USER_MANAGEMENT} element={<ProtectedAdminRoute><UserManagement /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.ANALYTICS} element={<ProtectedAdminRoute><Analytics /></ProtectedAdminRoute>} />
        <Route path={ADMIN_ROUTES.SYSTEM_SETTINGS} element={<ProtectedAdminRoute><SystemSettings /></ProtectedAdminRoute>} />

        {/* Admin Setup Password - Public (no auth required) */}
        <Route path="/admin/setup-password" element={<AdminSetupPassword />} />

        {/* Fallback /dashboard route - redirects based on user role */}
        <Route path="/dashboard" element={<Dashboard />} />

      </Routes>


    </Router>
  );
}

export default App;
